import { Button, TDropdownItem } from '@payaca/ambrosia';
import { useRegion } from '@/api/rest/users/useRegion';
import { backOff } from 'exponential-backoff';
import moment from 'moment-timezone';
import {
  ComponentProps,
  FC,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useHistory } from 'react-router-dom';

import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as invoicesActions from '@payaca/store/invoices/invoicesActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as uploadsActions from '@payaca/store/uploads/uploadsActions';

import useMarkGrantAsUnpaid from '@/api/mutations/grants/useMarkGrantAsUnpaid';
import {
  useGetInvoice,
  useGetInvoiceEmailTemplate,
} from '@/api/queries/invoice/useGetInvoice';
import { useSelector } from '@/api/state';
import { usePermissions, useUserHasPermission } from '@/hooks/usePermissions';
import BounceResendModal from '@/ui/components/bounceResendModal/BounceResendModal';
import ClientPortalPreviewButton from '@/ui/components/clientPortalPreview/ClientPortalPreviewButton';
import InvoiceDocument from '@/ui/components/invoiceDocument/InvoiceDocument';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import PreviewDocument from '@/ui/components/PreviewQuoteInvoice/PreviewDocument';
import RecordPaymentModal from '@/ui/components/recordPaymentModal/RecordPaymentModal';
import ResendModal from '@/ui/components/resendModal/ResendModal';
import { downloadFile } from '@/utils/download-file';
import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import { Modal } from '@payaca/ambrosia';

import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { MOMENT_DATE_SHORT_READABLE_FORMAT } from '@payaca/constants/momentFormatConstants';
import { getContactFirstName } from '@payaca/helpers/contactNameHelper';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { buildEmailSubjectPrefixForProjectFromProject } from '@payaca/helpers/dealHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getInvoicePLBadgeState } from '@payaca/helpers/invoiceHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { GrantsPermissions } from '@payaca/permissions/grants/grants.permissions';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import * as accountActions from '@payaca/store/account/accountActions';
import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import {
  useCustomerForDeal,
  useDeal,
  useInvoice,
} from '@payaca/store/hooks/appState';
import {
  getInvoiceGeneratedPdfUrl,
  requestGetInvoice,
  requestGetInvoicesForDeal,
  requestSendInvoice,
  voidInvoice,
} from '@payaca/store/invoices/invoicesActions';
import { requestGetJobPaymentsForDeal } from '@payaca/store/jobPayments/jobPaymentsActions';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './InvoicePage.sass';

type Props = {
  invoiceId: number;
  projectId: number;
};

const InvoicePage: FC<Props> = ({
  invoiceId,
  projectId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [showBounceResendModal, setShowBounceResendModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [isGettingPdfUrl, setIsGettingPdfUrl] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [showConfirmVoidInvoiceModal, setShowConfirmVoidInvoiceModal] =
    useState(false);

  const { userHasRequiredPermission } = usePermissions();

  const currentAccount = useSelector(
    (state: any) => state.users?.myProfile?.accounts[0]
  );

  useEffect(() => {
    dispatch(accountActions.requestGetEmailTemplates(currentAccount.id, true));

    return () => {
      dispatch(accountActions.clearEmailTemplates());
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'currentAccount.id'
  }, []);

  const invoice = useInvoice(invoiceId);

  const deal = useDeal(invoice?.dealId);

  // Grant details (whether this is a grant invoice and its grant line) aren't on
  // the Redux invoice, so read them from the GraphQL invoice — shares the query
  // cache with the email-template fetch below.
  const { data: graphqlInvoiceData } = useGetInvoice(invoiceId.toString());
  const grantLineId = graphqlInvoiceData?.invoice.isGrantInvoice
    ? graphqlInvoiceData.invoice.grantLine?.id
    : undefined;

  const { mutate: markGrantAsUnpaid } = useMarkGrantAsUnpaid({
    projectId: deal?.publicId ?? '',
    options: {
      onSuccess: () => {
        if (invoice) {
          dispatch(requestGetInvoice(invoice.id));
          dispatch(requestGetInvoicesForDeal(invoice.dealId));
          dispatch(requestGetDeal(invoice.dealId));
        }
        history.push(`/projects/${deal?.publicId}/invoices-and-payments`);
      },
    },
  });

  const companyName = useSelector(
    (state: any) => state.users.myProfile.accounts[0].companyName
  );

  const customer = useCustomerForDeal(invoice?.dealId);

  const customerContact = useMemo(() => {
    if (!customer) return;
    return getJobContactFromCustomer(customer, invoice?.contactId || null);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'customer'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: customer.contacts
  }, [customer?.contacts, invoice?.contactId]);

  const region = useRegion();

  const fetchPdfRetry = useCallback(() => {
    return backOff(
      async () => {
        return new Promise<void>((resolve, reject) => {
          dispatch(
            requestGetInvoice(invoiceId, (invoice) => {
              if (invoice.pdfUrl) {
                resolve();
              } else {
                reject(new Error('Invoice PDF is not ready yet'));
              }
            })
          );
        });
      },
      {
        numOfAttempts: 5,
        startingDelay: 1500,
        delayFirstAttempt: true,
      }
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [invoiceId]);

  useEffect(() => {
    if (invoice && !invoice.pdfUrl) {
      // Best-effort poll for the generated PDF. If it is still not ready after
      // all retries the UI falls back to on-demand generation, so swallow the
      // expected rejection to avoid an unhandled promise rejection.
      fetchPdfRetry().catch(() => {});
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a complex expression in the dependency array.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'invoice.pdfUrl', 'fetchPdfRetry', and 'invoice'
  }, [!invoice]);

  const onRecordOrConfirmPaymentSuccess = useCallback(() => {
    if (!invoice) return;
    dispatch(requestGetDeal(invoice.dealId));
    dispatch(requestGetInvoicesForDeal(invoice.dealId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'invoice'
  }, [dispatch, invoice?.dealId]);

  const dueDate = useMemo(() => {
    if (!invoice) return;
    return invoice.dueAt
      ? moment(invoice.dueAt).format(MOMENT_DATE_SHORT_READABLE_FORMAT)
      : moment()
          .add(invoice.dueInDays, 'days')
          .format(MOMENT_DATE_SHORT_READABLE_FORMAT);
  }, [invoice]);

  const { emailTemplate: invoiceHTMLEmailTemplate } =
    useGetInvoiceEmailTemplate(invoiceId.toString());

  const canGetEmailTemplates = useUserHasPermission({
    permissions: [AccountsPermissions.GET_EMAIL_TEMPLATES],
  });

  const emailTemplates = useSelector((state) => state.account.emailTemplates);

  const emailTemplate = useMemo(() => {
    return emailTemplates?.sendInvoice || '';
  }, [emailTemplates]);

  const emailSubjectPrefix = deal
    ? buildEmailSubjectPrefixForProjectFromProject(deal)
    : undefined;

  const resendConfig = useMemo<
    ComponentProps<typeof ResendModal>['resendConfig'] | undefined
  >(() => {
    if (emailSubjectPrefix === undefined) return undefined;
    return {
      handleResend: (requestData) => {
        dispatch(
          requestSendInvoice(
            {
              isResend: true,
              invoiceId: invoiceId,
              emailCopy: requestData.emailCopy,
            },
            () => {
              dispatch(requestGetInvoice(invoiceId));
            }
          )
        );

        return Promise.resolve();
      },
      documentType: 'invoice',
      emailConfig: {
        htmlTemplate: canGetEmailTemplates
          ? invoiceHTMLEmailTemplate?.preview
          : undefined,
        subject: `${emailSubjectPrefix}Your invoice from ${companyName}`,
        bodyTemplate: emailTemplate,
        substitutions: [
          {
            find: 'reference',
            replaceWith: invoice?.customReference || String(invoice?.reference),
          },
          { find: 'company_name', replaceWith: companyName },
          { find: 'customer_name', replaceWith: customerContact?.name },
          {
            find: 'customer_first_name',
            replaceWith: getContactFirstName(customerContact?.name),
          },
          {
            find: 'amount_due',
            replaceWith: currencyPrice(invoice?.dueValue || 0, region),
          },
          {
            find: 'due_date',
            replaceWith: dueDate,
          },
        ],
      },
    };
  }, [
    dispatch,
    invoiceId,
    canGetEmailTemplates,
    invoiceHTMLEmailTemplate?.preview,
    emailSubjectPrefix,
    companyName,
    emailTemplate,
    invoice?.customReference,
    invoice?.reference,
    customerContact?.name,
    invoice?.dueValue,
    region,
    dueDate,
  ]);

  useEffect(() => {
    if (invoiceId) {
      dispatch(invoicesActions.requestGetInvoiceLinesForInvoice(invoiceId));
      dispatch(jobContentActions.requestGetJobLineItemsForInvoice(invoiceId));
      dispatch(uploadsActions.requestGetUploadsForEntity(invoiceId, 'invoice'));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [invoiceId]);

  useEffect(() => {
    if (projectId) {
      dispatch(dealsActions.requestGetDeal(projectId));
      dispatch(invoicesActions.requestGetInvoicesForDeal(projectId));
      dispatch(requestGetJobPaymentsForDeal(projectId));
      dispatch(
        invoicesActions.requestGetPaymentReconciliationRecordsForDeal(projectId)
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [projectId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(customerActions.requestGetCustomer(deal.customerId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  useEffect(() => {
    if (invoice && !invoice?.sentAt && !invoice.voidedAt) {
      history.push(`/projects/${projectId}/invoices/${invoice.id}/edit`);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'history', 'invoice.id', 'projectId', and 'invoice'
  }, [invoice?.sentAt, invoice?.voidedAt]);

  const handleDownloadInvoicePDF = useCallback(() => {
    if (!invoice) return;
    setIsGettingPdfUrl(true);
    dispatch(
      getInvoiceGeneratedPdfUrl.request({
        invoiceId: invoiceId,
        callback: (pdfUrl: string) => {
          setIsGettingPdfUrl(false);
          downloadFile(
            pdfUrl,
            `invoice-${invoice.customReference || invoice.reference}.pdf`
          );
          requestGetInvoice(invoiceId);
        },
        onErrorCallback: () => setIsGettingPdfUrl(false),
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [invoiceId, invoice, setIsGettingPdfUrl]);

  const availableContextMenuItems = useMemo(() => {
    const actions: TDropdownItem[] = [];

    if (
      invoice &&
      !invoice.voidedAt &&
      !grantLineId &&
      invoice.dueValue > 0 &&
      userHasRequiredPermission([PaymentsPermissions.RECORD_PAYMENT])
    ) {
      actions.push({
        label: 'Record payment',
        onClick: () => setShowRecordPaymentModal(true),
      });
    }

    if (invoice?.sentAt && !invoice?.bouncedAt) {
      actions.push({
        label: 'Resend',
        onClick: () => setShowResendModal(true),
      });
    }

    // A grant invoice is reversed by marking the grant unpaid (which voids the
    // grant invoice and its payment together), so it offers that in place of the
    // plain "Void this invoice" action.
    if (
      grantLineId &&
      userHasRequiredPermission([GrantsPermissions.MARK_GRANT_AS_PAID])
    ) {
      actions.push({
        label: 'Mark grant as unpaid',
        onClick: () => markGrantAsUnpaid({ grantLineId }),
      });
    } else if (userHasRequiredPermission([InvoicesPermissions.VOID_INVOICE])) {
      actions.push({
        label: 'Void this invoice',
        onClick: () => setShowConfirmVoidInvoiceModal(true),
      });
    }

    return actions;
  }, [
    invoice,
    grantLineId,
    markGrantAsUnpaid,
    setShowRecordPaymentModal,
    setShowResendModal,
    setShowConfirmVoidInvoiceModal,
    userHasRequiredPermission,
  ]);

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={
        invoice
          ? [
              {
                label: 'Back to project',
                to: `/projects/${deal?.publicId}/invoices-and-payments`,
              },
              { label: 'Invoice' },
            ]
          : undefined
      }
    >
      <div className="p-4">
        <PreviewDocument
          title={
            graphqlInvoiceData?.invoice.isGrantInvoice
              ? 'Grant invoice'
              : 'Invoice'
          }
          badge={{
            variant: 'soft',
            colour: getInvoicePLBadgeState(invoice?.readableStatus),
            children: invoice?.readableStatus,
          }}
          firstAction={
            invoice?.pdfUrl
              ? {
                  children: (
                    <UntitledIcon name="download-01.3" className="h-5 w-5" />
                  ),
                  onClick: () => {
                    // todo: anchor link as a context menu item
                    window.open(invoice.pdfUrl, '_blank');
                  },
                }
              : {
                  children: (
                    <UntitledIcon name="download-01.3" className="h-5 w-5" />
                  ),
                  onClick: handleDownloadInvoicePDF,
                  isProcessing: isGettingPdfUrl,
                  disabled: isGettingPdfUrl,
                }
          }
          actions={availableContextMenuItems}
          leadingAction={
            // Only a sent Invoice — there is nothing for the Customer to have
            // seen of one they were never sent.
            invoice?.sentAt && deal?.publicId ? (
              <ClientPortalPreviewButton projectId={deal.publicId} />
            ) : undefined
          }
        >
          {invoice?.bouncedAt && (
            <FeedbackBlock feedbackLevel={FeedbackLevel.ALERT}>
              <p>The email sending this invoice to your customer has bounced</p>
              <PermissionGuard
                renderIfHasPermissions={[InvoicesPermissions.SEND_INVOICE]}
              >
                <Button
                  variant="link"
                  onClick={() => setShowBounceResendModal(true)}
                >
                  Retry
                </Button>
              </PermissionGuard>
            </FeedbackBlock>
          )}

          <InvoiceDocument isPreview invoiceId={invoiceId} />
        </PreviewDocument>
      </div>
      <RecordPaymentModal
        dealId={invoice?.dealId || 0}
        paymentTarget={{
          invoiceId: invoiceId,
        }}
        isOpen={showRecordPaymentModal}
        onClose={() => setShowRecordPaymentModal(false)}
        recordPaymentCallback={onRecordOrConfirmPaymentSuccess}
        confirmPaymentCallback={onRecordOrConfirmPaymentSuccess}
      />
      {deal?.customerId && resendConfig && (
        <BounceResendModal
          title="Resend invoice"
          isOpen={showBounceResendModal}
          onClose={() => setShowBounceResendModal(false)}
          customerId={deal.customerId}
          customerContactId={invoice?.contactId}
          resendConfig={resendConfig}
        />
      )}
      {deal?.customerId && resendConfig && (
        <ResendModal
          title="Resend invoice"
          isOpen={showResendModal}
          onClose={() => setShowResendModal(false)}
          customerId={deal.customerId}
          customerContactId={invoice?.contactId}
          resendConfig={resendConfig}
        />
      )}
      <Modal
        isOpen={showConfirmVoidInvoiceModal}
        onClose={() => setShowConfirmVoidInvoiceModal(false)}
        title={'Are you sure you want to void the invoice?'}
      >
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              colour="negative"
              onClick={() => {
                if (isVoiding) return;
                setIsVoiding(true);
                dispatch(
                  voidInvoice.request({
                    invoiceId: invoiceId,
                    callback: () => {
                      dispatch(requestGetInvoice(invoiceId));
                      setIsVoiding(false);
                      setShowConfirmVoidInvoiceModal(false);
                    },
                    onErrorCallback: () => setIsVoiding(false),
                  })
                );
              }}
              isProcessing={isVoiding}
              replaceContentWhenProcessing
            >
              Void
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </AuthenticatedPageWrapper>
  );
};
export default InvoicePage;
